(function () {
  'use strict';

  const config = window.MFSWishlistConfig || {};
  const endpoint = config.endpoint || '/apps/wishlist';
  const customerLoggedIn = !!config.customerLoggedIn;
  const localKey = 'mfs_wishlist_local_v2';
  const maxItems = 100;
  let syncQueue = Promise.resolve();
  let loadingCustomerWishlist = false;

  let state = Array.isArray(config.initialWishlist)
    ? config.initialWishlist.map(normalize)
    : [];

  /* -------------------------------------------------------
     Helpers
  ------------------------------------------------------- */

  function getImageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') {
      return image.startsWith('//') ? 'https:' + image : image;
    }
    if (typeof image === 'object') {
      const url = image.src || image.url || image.originalSrc || image.original_src || image.transformedSrc;
      if (typeof url === 'string') {
        return url.startsWith('//') ? 'https:' + url : url;
      }
    }
    return '';
  }

  function normalize(item) {
    if (!item) return null;

    const url = item.url || item.product_url || '';
    let inferredHandle = '';
    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/]+)/);
      if (match) inferredHandle = decodeURIComponent(match[1]);
    } catch (e) {}

    return {
      product_id: String(item.product_id || item.productId || item.id || ''),
      variant_id: item.variant_id || item.variantId ? String(item.variant_id || item.variantId) : '',
      selling_plan_id: item.selling_plan_id || item.sellingPlanId || item.selling_plan || '',
      title: item.title || item.product_title || '',
      url: url,
      image: getImageUrl(item.image || item.featured_image || item.image_url || ''),
      price: item.price !== undefined && item.price !== null ? item.price : (item.current_price !== undefined ? item.current_price : ''),
      compare_at_price: item.compare_at_price !== undefined && item.compare_at_price !== null ? item.compare_at_price : (item.original_price !== undefined ? item.original_price : ''),
      discount: item.discount || '',
      available: item.available !== false,
      quantity: Number(item.quantity || 1),
      handle: item.handle || inferredHandle,
      variants: Array.isArray(item.variants) ? item.variants : [],
      options: Array.isArray(item.options) ? item.options : [],
      requires_options: !!item.requires_options
    };
  }

  function keyOf(item) {
    return [
      String(item.product_id || ''),
      String(item.variant_id || '')
    ].join(':');
  }

  function getLocal() {
    try {
      const value = JSON.parse(localStorage.getItem(localKey) || '[]');
      return Array.isArray(value) ? value.map(normalize).filter(Boolean) : [];
    } catch (e) { return []; }
  }

  function saveLocal() {
    try { localStorage.setItem(localKey, JSON.stringify(state.slice(0, maxItems))); } catch (e) {}
  }

  function setState(items) {
    state = (Array.isArray(items) ? items : []).map(normalize).filter(Boolean).slice(0, maxItems);
    saveLocal();
    updateButtons();
    updateCount();
    renderWishlistPage();
  }

  function toast(message) {
    let el = document.querySelector('.mfs-wishlist-toast');

    if (!el) {
      el = document.createElement('div');
      el.className = 'mfs-wishlist-toast';
      document.body.appendChild(el);
    }

    el.textContent = message;
    el.classList.add('is-visible');

    clearTimeout(el._timer);

    el._timer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 2200);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatMoney(value) {
    if (value === null || value === undefined || value === '') return '';

    // Shopify product JSON returns prices in cents (integer). Some wishlist
    // responses serialize that integer as a string, e.g. "5997".
    let amount;
    if (typeof value === 'number') {
      amount = value / 100;
    } else {
      const raw = String(value).trim();
      if (!raw) return '';
      if (/^-?\d+$/.test(raw)) amount = Number(raw) / 100;
      else if (/^-?\d+\.\d{1,2}$/.test(raw)) amount = Number(raw);
      else return raw; // Preserve already formatted values such as "$59.97".
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  /* -------------------------------------------------------
     API
  ------------------------------------------------------- */

  async function api(method, body) {
    const options = {
      method: method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(endpoint, options);

    let data = {};

    try {
      data = await response.json();
    } catch (e) {}

    if (!response.ok) {
      throw new Error(
        data.message || data.error || 'Wishlist request failed'
      );
    }

    return data;
  }

  function compactForServer(items) {
    return (Array.isArray(items) ? items : []).map(function (item) {
      item = normalize(item);
      return {
        product_id: item.product_id,
        variant_id: item.variant_id,
        selling_plan_id: item.selling_plan_id,
        handle: item.handle,
        title: item.title,
        image: item.image
      };
    }).filter(function (item) { return /^\d+$/.test(item.product_id); }).slice(0, maxItems);
  }

  function mergeServerWithLocal(serverItems, localItems) {
    const localByKey = new Map();
    (localItems || []).forEach(function (item) {
      localByKey.set(keyOf(item), item);
    });

    return (serverItems || []).map(function (serverItem) {
      const normalized = normalize(serverItem);
      const local = localByKey.get(keyOf(normalized));
      if (!local) return normalized;

      // The customer metafield intentionally stores compact product identifiers.
      // Preserve display details from local storage when the server has no value.
      return Object.assign({}, local, normalized, {
        title: normalized.title || local.title,
        image: normalized.image || local.image,
        url: normalized.url || local.url,
        price: normalized.price || local.price,
        compare_at_price: normalized.compare_at_price || local.compare_at_price,
        discount: normalized.discount || local.discount,
        variants: normalized.variants && normalized.variants.length ? normalized.variants : local.variants,
        options: normalized.options && normalized.options.length ? normalized.options : local.options,
        quantity: normalized.quantity || local.quantity
      });
    });
  }

  function persistCustomerWishlist(items) {
    if (!customerLoggedIn) return Promise.resolve();
    const payload = compactForServer(items);
    syncQueue = syncQueue.catch(function () {}).then(function () {
      return api('POST', { wishlist: payload });
    });
    return syncQueue;
  }

  async function loadCustomerWishlist() {
    if (loadingCustomerWishlist) return;
    loadingCustomerWishlist = true;
    const localItems = getLocal();
    try {
      const data = await api('GET');
      const remoteItems = Array.isArray(data.wishlist) ? data.wishlist : [];
      if (!remoteItems.length && localItems.length) {
        // One-time migration: preserve existing browser-saved items for this logged-in customer.
        const migrated = compactForServer(localItems);
        await persistCustomerWishlist(localItems);
        setState(mergeServerWithLocal(migrated, localItems));
      } else {
        setState(mergeServerWithLocal(remoteItems, localItems));
      }
      refreshSavedProductDetails().catch(function (error) {
        console.warn('[Wishlist] Could not refresh saved product details', error);
      });
    } catch (error) {
      console.error('[Wishlist] Could not load customer wishlist', error);
      // Keep the current local list visible, but do not silently claim server sync succeeded.
      setState(localItems);
      toast('Wishlist could not sync. Please try again.');
    } finally {
      loadingCustomerWishlist = false;
    }
  }

  function loginWithReturn() {
    const currentUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;

    const returnUrl = encodeURIComponent(currentUrl);

    window.location.href =
      '/account/login?return_url=' + returnUrl;
  }

  /* -------------------------------------------------------
     Wishlist toggle
  ------------------------------------------------------- */

  async function toggle(item) {
    item = normalize(item);
    if (!item || !item.product_id) return;
    const previous = state.slice();
    const existing = state.some(function (x) { return keyOf(x) === keyOf(item); });
    const next = existing
      ? state.filter(function (x) { return keyOf(x) !== keyOf(item); })
      : [...state, item].slice(-maxItems);
    setState(next);
    if (customerLoggedIn) {
      try {
        await persistCustomerWishlist(next);
      } catch (error) {
        console.error('[Wishlist] Could not save wishlist', error);
        setState(previous);
        toast('Wishlist could not save. Please try again.');
        return;
      }
    }
    toast(existing ? 'Removed from wishlist' : 'Added to wishlist');
  }

  function getButtonItem(button) {
    return normalize({
      product_id: button.dataset.productId,
      variant_id: button.dataset.variantId,
      selling_plan_id: button.dataset.sellingPlanId || (button.closest('form') && button.closest('form').querySelector('[name="selling_plan"]') ? button.closest('form').querySelector('[name="selling_plan"]').value : (document.querySelector('form[action*="/cart/add"] [name="selling_plan"]') || document.querySelector('[name="selling_plan"]') || {}).value) || '',
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      compare_at_price: button.dataset.compareAtPrice,
      discount: button.dataset.discount,
      available: button.dataset.available !== 'false',
      quantity: 1,
      handle: button.dataset.productHandle
    });
  }

  function updateButtons() {
    const buttons = document.querySelectorAll(
      '[data-wishlist-button]'
    );

    buttons.forEach(function (button) {
      const item = getButtonItem(button);

      const active = state.some(function (wishlistItem) {
        return keyOf(wishlistItem) === keyOf(item);
      });

      button.classList.toggle('is-active', active);
      button.setAttribute(
        'aria-pressed',
        active ? 'true' : 'false'
      );

      button.setAttribute(
        'aria-label',
        active
          ? 'Remove from wishlist'
          : 'Add to wishlist'
      );

      const iconOutline =
        button.querySelector('[data-wishlist-outline]');

      const iconFilled =
        button.querySelector('[data-wishlist-filled]');

      if (iconOutline) {
        iconOutline.hidden = active;
      }

      if (iconFilled) {
        iconFilled.hidden = !active;
      }
    });
  }

  function updateCount() {
    const count = state.length;

    document
      .querySelectorAll('[data-wishlist-count]')
      .forEach(function (el) {
        el.textContent = count;

        el.hidden = count === 0;
      });
  }

  /* -------------------------------------------------------
     Wishlist page
  ------------------------------------------------------- */

  function renderWishlistPage() {
    const container = document.querySelector(
      '[data-wishlist-items]'
    );

    if (!container) {
      return;
    }

    const empty = document.querySelector(
      '[data-wishlist-empty]'
    );

    const pageContent = document.querySelector(
      '[data-wishlist-content]'
    );

    if (!state.length) {
      container.innerHTML = '';

      if (empty) {
        empty.hidden = false;
      }

      if (pageContent) {
        pageContent.hidden = true;
      }

      return;
    }

    if (empty) {
      empty.hidden = true;
    }

    if (pageContent) {
      pageContent.hidden = false;
    }

    container.innerHTML = state
      .map(renderWishlistItem)
      .join('');
  }

  function renderWishlistItem(item) {
    const price = formatMoney(item.price);
    const compareAt = formatMoney(item.compare_at_price);
    const hasCompare = compareAt && compareAt !== price;
    const discount = item.discount ? '<span class="mfs-wishlist-discount">' + escapeHtml(item.discount) + '</span>' : '';
    const stockText = item.available ? 'In stock' : 'Out of stock';
    const stockClass = item.available ? 'is-in-stock' : 'is-out-of-stock';
    return `
      <article class="mfs-wishlist-item" data-wishlist-item data-product-id="${escapeHtml(item.product_id)}" data-variant-id="${escapeHtml(item.variant_id)}" data-selling-plan-id="${escapeHtml(item.selling_plan_id || '')}">
        <div class="mfs-wishlist-item__image">
          ${item.url ? `<a href="${escapeHtml(item.url)}">` : ''}
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
          ${item.url ? '</a>' : ''}
        </div>
        <div class="mfs-wishlist-item__details">
          <a class="mfs-wishlist-item__title" href="${escapeHtml(item.url || '#')}">${escapeHtml(item.title)}</a>
          <div class="mfs-wishlist-item__stock ${stockClass}">${escapeHtml(stockText)}</div>
        </div>
        <div class="mfs-wishlist-item__price">
          <span class="mfs-wishlist-item__current-price">${escapeHtml(price)}</span>
          ${hasCompare ? `<span class="mfs-wishlist-item__compare-price">${escapeHtml(compareAt)}</span>` : ''}
          ${discount}
        </div>
        <div class="mfs-wishlist-item__actions">
          <div class="mfs-wishlist-quantity"><button type="button" data-wishlist-qty-minus aria-label="Decrease quantity">−</button><span data-wishlist-qty>${Number(item.quantity || 1)}</span><button type="button" data-wishlist-qty-plus aria-label="Increase quantity">+</button></div>
          <button type="button" class="mfs-wishlist-move-cart" data-wishlist-move-cart ${item.available ? '' : 'disabled'}>Move to Cart</button>
          <button type="button" class="mfs-wishlist-remove" data-wishlist-remove>Remove</button>
        </div>
      </article>`;
  }

  /* -------------------------------------------------------
     Remove item
  ------------------------------------------------------- */

  async function removeItem(item) {
    const previous = state.slice();
    const next = state.filter(function (x) { return keyOf(x) !== keyOf(item); });
    setState(next);
    if (customerLoggedIn) {
      try { await persistCustomerWishlist(next); }
      catch (error) {
        console.error('[Wishlist] Could not remove item', error);
        setState(previous);
        toast('Could not sync removal. Please try again.');
        return;
      }
    }
    toast('Removed from wishlist');
  }

  async function clearWishlist() {
    if (!state.length) return;
    const previous = state.slice();
    setState([]);
    if (customerLoggedIn) {
      try { await persistCustomerWishlist([]); }
      catch (error) {
        console.error('[Wishlist] Could not clear wishlist', error);
        setState(previous);
        toast('Could not sync wishlist clear. Please try again.');
        return;
      }
    }
    toast('Wishlist cleared');
  }

  function updateQuantity(item, quantity) {
    quantity = Math.max(
      1,
      Math.min(99, Number(quantity || 1))
    );

    const updated = state.map(function (x) {
      if (keyOf(x) === keyOf(item)) {
        return {
          ...x,
          quantity: quantity
        };
      }

      return x;
    });

    setState(updated);
    if (customerLoggedIn) {
      persistCustomerWishlist(updated).catch(function (error) {
        console.error('[Wishlist] Could not sync quantity change', error);
        toast('Quantity changed locally; sync failed. Please refresh and retry.');
      });
    }
  }

  /* -------------------------------------------------------
     Product JSON / Variant support
  ------------------------------------------------------- */

  let productCatalogPromise = null;

  async function findProductById(productId) {
    if (!productId) return null;

    if (!productCatalogPromise) {
      productCatalogPromise = (async function () {
        const allProducts = [];
        // Shopify's public products.json endpoint returns up to 250 products
        // per page. Walk pages so older products are not missed.
        for (let page = 1; page <= 40; page++) {
          const response = await fetch(
            '/products.json?limit=250&page=' + page,
            {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' }
            }
          );
          if (!response.ok) break;
          const data = await response.json();
          const products = Array.isArray(data.products) ? data.products : [];
          allProducts.push.apply(allProducts, products);
          if (products.length < 250) break;
        }
        return allProducts;
      })().catch(function (error) {
        productCatalogPromise = null;
        throw error;
      });
    }

    const products = await productCatalogPromise;
    return products.find(function (product) {
      return String(product.id) === String(productId);
    }) || null;
  }

  async function loadProductData(item) {
    // If the saved record only has IDs, recover the handle and product data
    // from Shopify's public product catalog instead of failing immediately.
    try {
      if (!item.handle) {
        const catalogProduct = await findProductById(item.product_id);
        if (catalogProduct) {
          item.handle = catalogProduct.handle || '';
          if ((!item.url || item.url === '#') && item.handle) {
            item.url = '/products/' + item.handle;
          }
          return catalogProduct;
        }
        return null;
      }

      const response = await fetch(
        '/products/' + encodeURIComponent(item.handle) + '.js',
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('[Wishlist] Product data recovery failed', error);
      return null;
    }
  }

  function findVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) {
      return null;
    }

    return product.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    });
  }

  /* -------------------------------------------------------
     Variant modal
  ------------------------------------------------------- */

  function closeVariantModal() {
    const modal = document.querySelector(
      '.mfs-wishlist-variant-modal'
    );

    if (modal) {
      modal.remove();
    }
  }

 function openVariantModal(item, product) {
  closeVariantModal();

  if (
    !product ||
    !Array.isArray(product.variants) ||
    product.variants.length <= 1
  ) {
    return addToCart(item.variant_id, item.quantity, item.selling_plan_id);
  }

  const availableVariants = product.variants.filter(function (variant) {
    return variant.available;
  });

  if (!availableVariants.length) {
    toast('This product is currently unavailable.');
    return;
  }

  if (!item.handle) {
    toast('Product URL not found. Please open the product page.');
    return;
  }

  const productUrl = '/products/' + encodeURIComponent(item.handle);

  // Reuse the theme's existing Quick Add modal.
  const openQuickAdd = function () {
    const modal = document.querySelector('quick-add-modal');

    if (!modal || typeof modal.show !== 'function') {
      toast('Theme Quick Add modal not found. Please refresh the page.');
      return;
    }

    let opener = document.getElementById('MfsWishlistQuickAddOpener');

    if (!opener) {
      opener = document.createElement('button');
      opener.id = 'MfsWishlistQuickAddOpener';
      opener.type = 'button';
      opener.tabIndex = -1;
      opener.setAttribute('aria-hidden', 'true');
      opener.style.position = 'fixed';
      opener.style.left = '-9999px';

      opener.innerHTML = `
        <span class="loading-overlay__spinner hidden">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </span>
      `;

      document.body.appendChild(opener);
    }

    opener.setAttribute('data-product-url', productUrl);
    modal.show(opener);
  };

  const existingModal = document.querySelector('quick-add-modal');

  if (existingModal && typeof existingModal.show === 'function') {
    openQuickAdd();
    return;
  }

  if (customElements.get('quick-add-modal')) {
    openQuickAdd();
    return;
  }

  customElements.whenDefined('quick-add-modal')
    .then(openQuickAdd)
    .catch(function () {
      toast('Unable to open product options. Please refresh the page.');
    });
}


  function renderVariantOptions(
    product,
    variants
  ) {
    return variants
      .map(function (variant, index) {
        const options = Array.isArray(
          variant.options
        )
          ? variant.options.join(' / ')
          : variant.title;

        return `
          <label class="mfs-wishlist-variant-option">
            <input
              type="radio"
              name="wishlist-variant"
              value="${escapeHtml(variant.id)}"
              data-wishlist-selected-variant
              ${index === 0 ? 'checked' : ''}
            >

            <span>
              ${escapeHtml(options)}
            </span>
          </label>
        `;
      })
      .join('');
  }

  /* Fetch product-page markup for selling plan and default gift selection. */
  async function fetchProductPageDocument(item) {
    if (!item || !item.url) return null;
    try {
      const response = await fetch(item.url, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (error) {
      console.warn('[Wishlist] Could not read product page', error);
      return null;
    }
  }

  async function resolveSellingPlanId(item) {
    if (item && item.selling_plan_id) return String(item.selling_plan_id);
    const doc = await fetchProductPageDocument(item);
    const planInput = doc && (doc.querySelector('form[action*="/cart/add"] [name="selling_plan"]') || doc.querySelector('[name="selling_plan"]'));
    return planInput && planInput.value ? String(planInput.value) : '';
  }

  /* Return the default checked free-gift variant configured on the product page. */
  async function resolveDefaultFreeGiftVariantId(item) {
    const doc = await fetchProductPageDocument(item);
    if (!doc) return '';

    const checkedGift = doc.querySelector('.gifts_selector-gift-choice:checked[name="items[1][id]"]') ||
      doc.querySelector('input[type="radio"][name="items[1][id]"]:checked');

    return checkedGift && checkedGift.value ? String(checkedGift.value) : '';
  }

  /* -------------------------------------------------------
     Add to cart
  ------------------------------------------------------- */

  async function addToCart(
    variantId,
    quantity,
    sellingPlanId,
    extraVariantIds
  ) {
    if (!variantId) {
      toast('Please select a product option.');
      return;
    }

    try {
      const response = await fetch(
        '/cart/add.js',
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            'Accept':
              'application/json'
          },
          body: JSON.stringify({
            items: [
              Object.assign(
                {
                  id: Number(variantId),
                  quantity: Number(quantity || 1)
                },
                sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}
              )
            ].concat(
              (Array.isArray(extraVariantIds) ? extraVariantIds : [])
                .filter(function (id) { return id && String(id) !== String(variantId); })
                .map(function (id) { return { id: Number(id), quantity: 1 }; })
            )
          })
        }
      );

      if (!response.ok) {
        const errorData =
          await response.json().catch(
            function () {
              return {};
            }
          );

        throw new Error(
          errorData.description ||
          'Unable to add to cart'
        );
      }

      toast('Added to cart');


// Wishlist Add-to-Cart Analytics
try {
  if (customerLoggedIn) {
    const wishlistItem = state.find(function (item) {
      return String(item.variant_id) === String(variantId);
    });

    if (wishlistItem && wishlistItem.product_id) {
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          wishlist: state,
          wishlist_atc: {
            product_id: String(wishlistItem.product_id)
          }
        })
      });
    }
  }
} catch (error) {
  console.error('[Wishlist Analytics] Tracking failed:', error);
}



      if (typeof window.openCartDrawer === 'function') {
  window.openCartDrawer(null, true);
}

      /*
       * Refresh the wishlist after moving
       * the item to cart.
       */
      await removeByVariant(
        variantId
      );

      /*
       * Let existing theme cart
       * functionality refresh itself.
       */
      document.dispatchEvent(
        new CustomEvent(
          'mfs:wishlist:cart-added',
          {
            detail: {
              variantId: variantId
            }
          }
        )
      );

    } catch (error) {
      console.error(
        '[Wishlist] Cart error',
        error
      );

      toast(
        error.message ||
        'Unable to add to cart'
      );
    }
  }

  async function removeByVariant(variantId) {
    const item = state.find(function (x) {
      return String(x.variant_id) === String(variantId);
    });
    if (!item) return;
    /* Silent removal after successful cart add; preserve the Added to cart toast. */
    setState(state.filter(function (x) { return keyOf(x) !== keyOf(item); }));
  }

  /* -------------------------------------------------------
     Event delegation
  ------------------------------------------------------- */

  document.addEventListener(
    'click',
    async function (event) {

      const wishlistButton =
        event.target.closest(
          '[data-wishlist-button]'
        );

      if (wishlistButton) {
        event.preventDefault();
        event.stopPropagation();

        if (!customerLoggedIn) {
          /*
           * Guest users can save locally.
           */
          await toggle(
            getButtonItem(
              wishlistButton
            )
          );

          return;
        }

        await toggle(
          getButtonItem(
            wishlistButton
          )
        );

        return;
      }

      const removeButton =
        event.target.closest(
          '[data-wishlist-remove]'
        );

      if (removeButton) {
        event.preventDefault();

        const row =
          removeButton.closest(
            '[data-wishlist-item]'
          );

        if (!row) return;

        const item = state.find(
          function (x) {
            return (
              String(x.product_id) ===
                String(
                  row.dataset.productId
                ) &&
              String(x.variant_id || '') ===
                String(
                  row.dataset.variantId || ''
                )
            );
          }
        );

        if (item) {
          await removeItem(item);
        }

        return;
      }

      const clearButton =
        event.target.closest(
          '[data-wishlist-clear]'
        );

      if (clearButton) {
        event.preventDefault();

        await clearWishlist();

        return;
      }

      const minus =
        event.target.closest(
          '[data-wishlist-qty-minus]'
        );

      if (minus) {
        event.preventDefault();

        const row =
          minus.closest(
            '[data-wishlist-item]'
          );

        if (!row) return;

        changeRowQuantity(row, -1);

        return;
      }

      const plus =
        event.target.closest(
          '[data-wishlist-qty-plus]'
        );

      if (plus) {
        event.preventDefault();

        const row =
          plus.closest(
            '[data-wishlist-item]'
          );

        if (!row) return;

        changeRowQuantity(row, 1);

        return;
      }

      const moveButton =
        event.target.closest(
          '[data-wishlist-move-cart]'
        );

      if (moveButton) {
        event.preventDefault();

        const row =
          moveButton.closest(
            '[data-wishlist-item]'
          );

        if (!row) return;

        const item = state.find(
          function (x) {
            return (
              String(x.product_id) ===
                String(
                  row.dataset.productId
                ) &&
              String(x.variant_id || '') ===
                String(
                  row.dataset.variantId || ''
                )
            );
          }
        );

        if (!item) return;

        const itemTitle = String(item.title || '');
        const itemUrl = String(item.url || '');
        const isSubscriptionProduct =
          /subscription/i.test(itemTitle) ||
          /subscription/i.test(itemUrl) ||
          /mindful-soul-box-monthly-subscription/i.test(itemUrl);

        moveButton.disabled = true;

        try {
          if (isSubscriptionProduct) {
            const sellingPlanId = await resolveSellingPlanId(item);
            if (!sellingPlanId) {
              toast('Could not find the subscription plan. Please refresh the product page and try again.');
              return;
            }
            const freeGiftVariantId = await resolveDefaultFreeGiftVariantId(item);
            await addToCart(
              item.variant_id,
              item.quantity,
              sellingPlanId,
              freeGiftVariantId ? [freeGiftVariantId] : []
            );
            return;
          }

          const product =
            await loadProductData(item);

          if (
            product &&
            Array.isArray(product.variants) &&
            product.variants.length > 1
          ) {
            openVariantModal(
              item,
              product
            );
          } else {
            await addToCart(
              item.variant_id ||
                (
                  product &&
                  product.variants &&
                  product.variants[0]
                    ? product.variants[0].id
                    : ''
                ),
              item.quantity,
              item.selling_plan_id
            );
          }
        } finally {
          moveButton.disabled = false;
        }

        return;
      }
    },
    true
  );

  /* Place Wishlist after Blog in the mobile drawer, matching the supplied Figma. */
  function placeWishlistAfterBlog() {
    const drawer = document.querySelector('.menu_drawer.mobile');
    if (!drawer) return;
    const wishlist = drawer.querySelector('.mfs-mobile-drawer-wishlist');
    const menu = drawer.querySelector('.menu_drawer-body-main');
    if (!wishlist || !menu) return;
    const links = Array.from(menu.querySelectorAll('a'));
    const blogLink = links.find(function (link) {
      return (link.textContent || '').trim().toLowerCase() === 'blog' ||
        (link.getAttribute('href') || '').toLowerCase().includes('/blogs');
    });
    if (blogLink) blogLink.insertAdjacentElement('afterend', wishlist);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeWishlistAfterBlog, { once: true });
  } else {
    placeWishlistAfterBlog();
  }

  function changeRowQuantity(
    row,
    difference
  ) {
    const productId =
      row.dataset.productId;

    const variantId =
      row.dataset.variantId || '';

    const item = state.find(
      function (x) {
        return (
          String(x.product_id) ===
            String(productId) &&
          String(x.variant_id || '') ===
            String(variantId)
        );
      }
    );

    if (!item) return;

    updateQuantity(
      item,
      Number(item.quantity || 1) +
        difference
    );
  }

  /* -------------------------------------------------------
     Initialise
  ------------------------------------------------------- */

  async function refreshSavedProductDetails() {
    let changed = false;
    const unresolved = [];

    for (const item of state) {
      const needsDetails = !item.title || !item.image || item.price === '' || item.price === null || item.price === undefined || !item.url;
      if (!needsDetails) continue;

      const product = await loadProductData(item);
      if (!product) {
        unresolved.push(item);
        continue;
      }

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find(function (v) {
        return String(v.id) === String(item.variant_id);
      }) || variants[0];

      if (!item.title && product.title) { item.title = product.title; changed = true; }
      const productImage = getImageUrl(product.featured_image) ||
        getImageUrl(product.images?.[0]) ||
        getImageUrl(product.image);
      if (!item.image && productImage) {
        item.image = productImage;
        changed = true;
      }
      if ((!item.url || item.url === '#') && product.url) { item.url = product.url; changed = true; }
      if (!item.handle && product.handle) { item.handle = product.handle; changed = true; }
      if (variant) {
        if (item.price === '' || item.price === null || item.price === undefined) { item.price = variant.price; changed = true; }
        if ((item.compare_at_price === '' || item.compare_at_price === null || item.compare_at_price === undefined) && variant.compare_at_price) { item.compare_at_price = variant.compare_at_price; changed = true; }
        item.available = variant.available !== false;
      }
    }

    // Retry unresolved items through the public product catalog. This covers
    // records that have a stale/missing handle as well as IDs-only server records.
    if (unresolved.length) {
      for (const item of unresolved) {
        try {
          const product = await findProductById(item.product_id);
          if (!product) continue;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const variant = variants.find(function (v) {
            return String(v.id) === String(item.variant_id);
          }) || variants[0];

          if (!item.title && product.title) { item.title = product.title; changed = true; }
          if (!item.handle && product.handle) { item.handle = product.handle; changed = true; }
          if ((!item.url || item.url === '#') && product.handle) {
            item.url = '/products/' + product.handle;
            changed = true;
          }
          const image = getImageUrl(product.featured_image) ||
            getImageUrl(product.images?.[0]) ||
            getImageUrl(product.image);
          if (!item.image && image) { item.image = image; changed = true; }
          if (variant) {
            if (item.price === '' || item.price === null || item.price === undefined) { item.price = variant.price; changed = true; }
            if ((item.compare_at_price === '' || item.compare_at_price === null || item.compare_at_price === undefined) && variant.compare_at_price) { item.compare_at_price = variant.compare_at_price; changed = true; }
            item.available = variant.available !== false;
          }
        } catch (error) {
          console.warn('[Wishlist] Could not recover product ID ' + item.product_id, error);
        }
      }
    }

    if (changed) {
      saveLocal();
      updateButtons();
      updateCount();
      renderWishlistPage();
    }
  }

  function init() {
    state = getLocal();
    updateButtons();
    updateCount();
    renderWishlistPage();
    if (customerLoggedIn) {
      loadCustomerWishlist();
    } else {
      refreshSavedProductDetails().catch(function (error) {
        console.warn('[Wishlist] Could not refresh saved product details', error);
      });
    }
  }

  
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  /*
   * Public API for other wishlist/theme
   * components if required later.
   */
  window.MFSWishlist = {
    getItems: function () {
      return state.slice();
    },

    isInWishlist: function (
      productId,
      variantId
    ) {
      return state.some(
        function (item) {
          return (
            String(item.product_id) ===
              String(productId) &&
            String(item.variant_id || '') ===
              String(variantId || '')
          );
        }
      );
    },

    add: function (item) {
      return toggle(item);
    },

    remove: function (item) {
      return removeItem(
        normalize(item)
      );
    },

    clear: function () {
      return clearWishlist();
    }
  };
})();


(function () {
  const WISHLIST_KEY = 'mfs_wishlist_local_v2';

  function updateWishlistBadges() {
    let count = 0;

    try {
      const saved = JSON.parse(
        localStorage.getItem(WISHLIST_KEY) || '[]'
      );

      count = Array.isArray(saved) ? saved.length : 0;
    } catch (error) {
      count = 0;
    }

    document.querySelectorAll('[data-wishlist-count]')
      .forEach(function (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      updateWishlistBadges
    );
  } else {
    updateWishlistBadges();
  }

  document.addEventListener(
    'click',
    function () {
      setTimeout(updateWishlistBadges, 100);
    }
  );

  window.addEventListener('storage', updateWishlistBadges);

  window.updateMFSWishlistBadges = updateWishlistBadges;
})();
